import type { Configuration } from "@azure/msal-browser";
import { LogLevel } from "@azure/msal-browser";

// Config-driven per env (TS-* project-structure convention): authority/clientId/scopes never
// hardcoded. `VITE_API_SCOPE` is a placeholder pending the Entra app registration exposing a
// named scope for this API (see decisions.md D-221) - until then it defaults to `.default` over
// the API's own client id, which Entra accepts for a confidential-client-style API exposure.
//
// Read lazily (function calls, not module-level consts): import.meta.env is evaluated once per
// module instance, and a module is imported once per process - a module-level const would freeze
// these values at first import, which is both wrong for a test that stubs the env after import
// and (more importantly) not actually how Vite's static replacement works across a dev-server
// reload versus a fresh build, so a function is the only form that is honest about being re-read.
function tenantId(): string | undefined {
  return import.meta.env.VITE_ENTRA_TENANT_ID;
}

function clientId(): string | undefined {
  return import.meta.env.VITE_ENTRA_CLIENT_ID;
}

function configuredApiScope(): string | undefined {
  return import.meta.env.VITE_API_SCOPE;
}

export function isAuthConfigured(): boolean {
  return Boolean(tenantId() && clientId());
}

export function buildMsalConfig(): Configuration {
  return {
    auth: {
      clientId: clientId() ?? "",
      authority: `https://login.microsoftonline.com/${tenantId() ?? "common"}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: "sessionStorage",
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
        loggerCallback: () => {
          // Intentionally silent in production; MSAL's own telemetry is not this app's audit log.
        },
      },
    },
  };
}

/** The scope requested for API access tokens (SignalR uses the same token). */
export function apiScopes(): string[] {
  const scope = configuredApiScope();
  if (scope) return [scope];
  const id = clientId();
  return id ? [`api://${id}/.default`] : [];
}
